<script setup>
import { ref, onMounted } from 'vue';
import { useService } from "@/composables/useService";

const props = defineProps({
  isOpen: Boolean,
  parcel: { type: Object, default: null },
  orders: { type: Array, default: () => [] }
});

const emit = defineEmits(['close']);
const { getServicesApi } = useService();
const services = ref([]);

const statusOrder = ref([
    { id: 10, name: "order-new-wait-conf", display_name: "Mới - Chờ xác nhận" },
    { id: 11, name: "order-conf-wait-parcel", display_name: "Đã xác nhận - Chờ hàng về" },
    { id: 12, name: "order-in-warehouse-wait-package", display_name: "Hàng về kho - Chờ đóng gói" },
    { id: 14, name: "order-transited-wait-complete", display_name: "Đang vận chuyển" },
    { id: 15, name: "order-completed", display_name: "Hoàn thành" },
    { id: 16, name: "order-return", display_name: "Hàng bị trả về" },
    { id: 17, name: "order-reserve", display_name: "Chờ lưu kho" },
    { id: 18, name: "order-destroy", display_name: "Hủy" },
    { id: 19, name: "order-update-wait-conf", display_name: "Phí thay đổi - Chờ xác nhận" },
    { id: 20, name: "order-loss", display_name: "Thất lạc" },
    { id: 21, name: "order-reimbursement", display_name: "Bồi thường" },
]);

const getStatusNameForOrder = (id) => {
  const status = statusOrder.value.find(s => s.id === id);
  return status ? status.display_name : 'Không xác định';
};

const getStatusClassForOrder = (id) => {
  const map = {
    10: 'status-new',
    11: 'status-conf',
    12: 'status-wh',
    14: 'status-transmit',
    15: 'status-done',
    16: 'status-return',
    17: 'status-reserve',
    18: 'status-cancel',
    19: 'status-update',
    20: 'status-loss',
    21: 'status-reimburse',
  };
  return map[id] || 'status-default';
};

const getServiceName = (id) => {
  if (!Array.isArray(services.value)) return 'Không xác định';
  const service = services.value.find(s => s.id === id);
  return service ? service.name : 'Không xác định';
};

const fetchServices = async () => {
    try {
        const res = await getServicesApi({ limit: -1, page: 1 });
        if (res.success && res.data) {
            // API trả về format { data: [...] } nên cần lấy res.data.data
            services.value = res.data.data || [];
        }
    } catch (err) {
        console.error("Lỗi khi tải danh sách dịch vụ: ", err);
    }
}

onMounted(() => {
    fetchServices();
});

const close = () => {
  emit('close');
};
</script>

<template>
  <div v-if="isOpen" class="modal-overlay" @click.self="close">
    <div class="modal-content custom-modal">
      <div class="modal-header d-flex justify-content-between align-items-center">
        <h5 class="m-0">Orders in Parcel: {{ parcel?.code }}</h5>
        <button class="btn-close" @click="close"></button>
      </div>
      <div class="modal-body">
        <div class="table-responsive">
          <table class="table table-hover table-bordered">
            <thead class="bg-light">
              <tr>
                <th>Order Code</th>
                <th>Receiver</th>
                <th>Service</th>
                <th>Fee</th>
                <th>Tracking Number</th>
                <th>Status</th>
                <th>Weight</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="order in orders" :key="order.id">
                <td class="fw-bold text-primary">{{ order.code }}</td>
                <td>
                  <div class="small">
                    <div><strong>{{ order.receiver_name }}</strong></div>
                    <div class="text-muted">{{ order.receiver_phone }}</div>
                  </div>
                </td>
                <td>{{ getServiceName(order.service_id) }}</td>
                <td class="text-end fw-bold">{{ Number(order.total_order_fee || 0).toLocaleString() }}</td>
                <td>{{ order.tracking_number || '---' }}</td>
                <td>
                  <span class="status-badge" :class="getStatusClassForOrder(order.status_id)">
                    {{ getStatusNameForOrder(order.status_id) }}
                  </span>
                </td>
                <td class="text-center font-weight-bold">{{ order.weight }} kg</td>
              </tr>
              <tr v-if="orders.length === 0">
                <td colspan="7" class="text-center py-4 text-muted italic">
                  Chưa có đơn hàng nào trong kiện này.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div class="modal-footer">
        <button class="btn btn-secondary" @click="close">Close</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}
.custom-modal {
  background: white;
  width: 90%;
  max-width: 900px;
  max-height: 90vh;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}
.modal-header { padding: 15px 20px; border-bottom: 1px solid #eee; }
.modal-body { padding: 20px; overflow-y: auto; }
.modal-footer { padding: 15px 20px; border-top: 1px solid #eee; display: flex; justify-content: flex-end; }

/* Status Badge inside modal */
.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
}
.status-new { background-color: #e0f2fe; color: #0369a1; }
.status-conf { background-color: #e0e7ff; color: #4338ca; }
.status-wh { background-color: #ffedd5; color: #c2410c; }
.status-transmit { background-color: #dbeafe; color: #1d4ed8; }
.status-done { background-color: #d1fae5; color: #047857; }
.status-return { background-color: #f3e8ff; color: #7e22ce; }
.status-reserve { background-color: #ccfbf1; color: #0f766e; }
.status-cancel { background-color: #fee2e2; color: #b91c1c; }
.status-update { background-color: #fce7f3; color: #be185d; }
.status-loss { background-color: #111827; color: #ffffff; }
.status-reimburse { background-color: #fef9c3; color: #a16207; }
.status-default { background-color: #f9fafb; color: #6b7280; }
</style>
